import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import '../styles/Footer.css';

const Footer: React.FC = () => {
  const ctaSectionRef = useRef<HTMLDivElement>(null);
  const particleRefs = useRef<(HTMLDivElement | null)[]>([]);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const paragraphRef = useRef<HTMLParagraphElement>(null);
  const buttonContainerRef = useRef<HTMLDivElement>(null);
  const buttonGlowRef = useRef<HTMLDivElement>(null);
  
  // Track mouse position
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!ctaSectionRef.current) return;
      
      const rect = ctaSectionRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      // Update state with current mouse position
      setMousePosition({ x, y });
      
      // Move each particle with different speeds and delays for parallax effect
      particleRefs.current.forEach((particle, index) => {
        if (!particle) return;
        
        // Calculate distance from mouse
        const speed = 0.15 - (index * 0.03); // Different speeds for each particle
        
        // Update particle positions with parallax effect
        const moveX = x - rect.width / 2;
        const moveY = y - rect.height / 2;
        
        // Calculate new positions
        const newX = moveX * speed * -1;
        const newY = moveY * speed * -1;
        
        // Delay increases with each particle
        const delay = index * 50;
        
        // Apply movement and show particle
        setTimeout(() => {
          particle.style.transform = `translate(${newX}px, ${newY}px)`;
          particle.style.opacity = '1';
        }, delay);
      });
      
      // Subtle parallax for content
      if (headingRef.current) {
        const headingSpeed = 0.02;
        headingRef.current.style.transform = `translate(${(rect.width/2 - x) * headingSpeed}px, ${(rect.height/2 - y) * headingSpeed}px)`;
      }
      
      if (paragraphRef.current) {
        const paragraphSpeed = 0.01;
        paragraphRef.current.style.transform = `translate(${(rect.width/2 - x) * paragraphSpeed}px, ${(rect.height/2 - y) * paragraphSpeed}px)`;
      }
      
      if (buttonContainerRef.current) {
        const buttonSpeed = 0.03;
        buttonContainerRef.current.style.transform = `translate(${(rect.width/2 - x) * buttonSpeed}px, ${(rect.height/2 - y) * buttonSpeed}px)`;
      }
      
      // Button glow follows mouse closely
      if (buttonGlowRef.current) {
        buttonGlowRef.current.style.opacity = '1';
        buttonGlowRef.current.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`;
      }
    };
    
    const handleMouseLeave = () => {
      // Reset particles when mouse leaves
      particleRefs.current.forEach((particle) => {
        if (!particle) return;
        particle.style.transform = 'translate(0, 0)';
        particle.style.opacity = '0';
      });
      
      // Reset content
      if (headingRef.current) {
        headingRef.current.style.transform = 'translate(0, 0)';
      }
      
      if (paragraphRef.current) {
        paragraphRef.current.style.transform = 'translate(0, 0)';
      }
      
      if (buttonContainerRef.current) {
        buttonContainerRef.current.style.transform = 'translate(0, 0)';
      }
      
      // Hide button glow
      if (buttonGlowRef.current) {
        buttonGlowRef.current.style.opacity = '0';
      }
    };
    
    const sectionEl = ctaSectionRef.current;
    if (sectionEl) {
      sectionEl.addEventListener('mousemove', handleMouseMove);
      sectionEl.addEventListener('mouseleave', handleMouseLeave);
    }
    
    return () => {
      if (sectionEl) {
        sectionEl.removeEventListener('mousemove', handleMouseMove);
        sectionEl.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, []);
  
  // Correctly typed ref callbacks
  const setParticleRef = (index: number) => (el: HTMLDivElement | null) => {
    particleRefs.current[index] = el;
  };
  
  return (
    <>
      <div className="cta-section" ref={ctaSectionRef}>
        <div className="cta-particle-container">
          {/* Animated particles */}
          <div className="cta-particle" ref={setParticleRef(0)}></div>
          <div className="cta-particle" ref={setParticleRef(1)}></div>
          <div className="cta-particle" ref={setParticleRef(2)}></div>
        </div>
        
        <h2 ref={headingRef}>Ready to Transform Your Business?</h2>
        <p ref={paragraphRef}>Partner with Betaque to leverage cutting-edge technology solutions tailored to your specific challenges.</p>
        
        <div ref={buttonContainerRef} className="cta-button-container">
          <Link to="/contact" className="cta-button">
            Get Started <span className="arrow">→</span>
          </Link>
        </div>
        
        {/* Button glow effect */}
        <div className="cta-button-glow" ref={buttonGlowRef}></div>
      </div>
      
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-section">
            <h3>betaque.</h3>
            <ul>
              <li><Link to="/why-us">Why Us</Link></li>
              <li><Link to="/services">Services</Link></li>
              <li><Link to="/industries">Industries</Link></li>
              <li><Link to="/security">Security</Link></li>
            </ul>
          </div>

          <div className="footer-section">
            <h3>Services</h3>
            <ul>
              <li><Link to="/services/cloud-cost-optimization">Cloud Cost Optimization</Link></li>
              <li><Link to="/services/kubernetes-consulting">Kubernetes Consulting</Link></li>
              <li><Link to="/services/terraform-pulumi">Terraform Pulumi</Link></li>
              <li><Link to="/services/sre-consulting">SRE-Consulting</Link></li>
              <li><Link to="/services/prometheus-grafana">Prometheus-Grafana</Link></li>
            </ul>
          </div>

          <div className="footer-section">
            <h3>Industries</h3>
            <ul>
              <li><Link to="/industries/finance-banking">FinTech & BFSI</Link></li>
              <li><Link to="/industries/government-public-sector">Government & Public Sector</Link></li>
              <li><Link to="/industries/healthcare-life-sciences">Healthcare</Link></li>
              <li><Link to="/industries/retail-ecommerce">E-commerce & Retail</Link></li>
              <li><Link to="/industries/manufacturing-industry-4">Manufacturing Industry 4.O</Link></li>
            </ul>
          </div>

          {/* <div className="footer-section">
            <h3>Resources</h3>
            <ul>
              <li><Link to="/blog">Blog</Link></li>
              <li><Link to="/case-studies">Case Studies</Link></li>
            </ul>
          </div> */}

          <div className="footer-section">
            <h3>Connect</h3>
            <ul>
              <li><a href="https://github.com/betaque" target="_blank" rel="noopener noreferrer">GitHub</a></li>
              <li><a href="https://linkedin.com/company/betaque" target="_blank" rel="noopener noreferrer">LinkedIn</a></li>
              <li><a href="https://twitter.com/betaque" target="_blank" rel="noopener noreferrer">Twitter</a></li>
              <li><Link to="/contact">Contact Us</Link></li>
            </ul>
          </div>
        </div>

        <div className="footer-bottom">
          <div className="copyright">
            © {new Date().getFullYear()} betaque.
          </div>
          <div className="footer-links">
            <Link to="/privacy-policy">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer; 